import { Card, CardContent, Box, Typography, useTheme } from '@mui/material';
import { keyframes } from '@mui/system';
import { useState, useEffect } from 'react';

// Smooth entrance animation
const smoothSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Value shimmer on update
const valueShimmer = keyframes`
  0% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(1.03);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`;

/**
 * MetricCard - Premium Financial Metric Display
 *
 * Displays CFO-level financial metrics with HUGE 72px values that dominate.
 * Features refined typography, subtle gradients, and sophisticated hover states.
 *
 * @param {string} title - Metric category label (12px uppercase, de-emphasized)
 * @param {string} value - Primary metric value (72px, THE STAR)
 * @param {string} subtitle - Supporting context (optional)
 * @param {number} percentage - Progress percentage for context bar (optional)
 * @param {string} color - MUI color variant (primary|success|info|warning|error|default)
 */
export default function MetricCard({
  title,
  value,
  subtitle,
  percentage,
  color = 'primary'
}) {
  const theme = useTheme();
  const [shimmer, setShimmer] = useState(false);

  // Trigger shimmer animation when value changes
  useEffect(() => {
    setShimmer(true);
    const timer = setTimeout(() => setShimmer(false), 300);
    return () => clearTimeout(timer);
  }, [value]);

  return (
    <Card
      sx={{
        height: '100%',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? `radial-gradient(circle at top right, ${theme.palette.primary.main}10, transparent)`
            : `radial-gradient(circle at top right, ${theme.palette.primary.main}08, white)`,
        border: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        borderRadius: 2,
        position: 'relative',
        overflow: 'visible',
        animation: `${smoothSlideIn} 0.6s cubic-bezier(0.22, 0.61, 0.36, 1)`,
        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',

        // Top accent stripe
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: (theme) =>
            `linear-gradient(90deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
        },

        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? `0 12px 32px ${theme.palette[color].main}33`
              : `0 12px 32px ${theme.palette[color].main}30`,

          '& .metric-value': {
            background: (theme) =>
              `linear-gradient(135deg, ${theme.palette[color].main}, ${theme.palette[color].light})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          },
        },
      }}
    >
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          p: 3,
          '&:last-child': { pb: 3 },
        }}
      >
        {/* Tiny uppercase label - DE-EMPHASIZED */}
        <Typography
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: 'grey.500',
            mb: 1,
          }}
        >
          {title}
        </Typography>

        {/* HUGE value - THE STAR */}
        <Typography
          className={`metric-value ${shimmer ? 'shimmer' : ''}`}
          sx={{
            fontSize: 'clamp(1.5rem, 3.5vw, 2.5rem)', // Fluid: 24px → 40px, fits in card
            fontWeight: 800,
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            color: `${color}.main`,
            mb: 2,
            transition: 'all 0.3s ease',
            fontFamily: theme.typography.fontFamilyMono,
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap', // Prevent awkward line breaks
            '&.shimmer': {
              animation: `${valueShimmer} 0.3s ease`,
            },
          }}
        >
          {value}
        </Typography>

        {/* Progress context bar (optional) */}
        {subtitle && (
          <Box sx={{ mt: 'auto' }}>
            {percentage !== undefined && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    height: 4,
                    bgcolor: 'grey.200',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box
                    sx={{
                      width: `${Math.min(Math.max(percentage, 0), 100)}%`,
                      height: '100%',
                      bgcolor: `${color}.main`,
                      borderRadius: 2,
                      transition: 'width 1s ease',
                    }}
                  />
                </Box>
              </Box>
            )}
            <Typography
              variant="caption"
              sx={{
                color: 'grey.500',
                fontSize: '0.8125rem',
                lineHeight: 1.4,
              }}
            >
              {subtitle}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
